import type {
	ShortcutBindingsMap,
	ShortcutConflictEntry,
	ShortcutPresetName,
	ShortcutsStoreState,
} from 'src/stores/shortcuts/types';

import { defineStore } from 'pinia';
import {
	SHORTCUT_BINDINGS_KEY,
	SHORTCUT_ENABLED_KEY,
	SHORTCUT_PRESET_NAME_KEY,
} from 'src/stores/shortcuts/localStorageKeys';
import createDefaultShortcutsState, {
	resolveShortcutPresetName,
	sanitizeShortcutBindings,
} from 'src/stores/shortcuts/state';
import { UpdateTheUserPreferenceCommand } from 'src/stores/userInterfaceSettings/commands/UpdateTheUserPreferenceCommand';

import { SHORTCUT_SETTINGS_PREFERENCE_KEY } from 'src/models/userInterfaceSettings';

import { CommandBus } from 'src/util/command/CommandBus';
import { migrateBindingIds } from 'src/util/shortcuts/bindingMigrations';
import { findShortcutConflicts } from 'src/util/shortcuts/conflictDetector';
import { buildDefaultBindings } from 'src/util/shortcuts/defaultBindings';
import { normalizeKeys } from 'src/util/shortcuts/keyNormalizer';

const createDefaultState = (): ShortcutsStoreState => createDefaultShortcutsState();

function persistBindings(bindings: ShortcutBindingsMap): void {
	localStorage.setItem(SHORTCUT_BINDINGS_KEY, JSON.stringify(bindings));
}

export const useShortcutsStore = defineStore('shortcuts', {
	actions: {
		applyPreset(presetName: ShortcutPresetName, commandBus?: CommandBus): void {
			const effectivePreset: ShortcutPresetName = presetName === 'custom' ? 'minimal' : presetName;
			this.bindings = buildDefaultBindings(effectivePreset);
			this.presetName = presetName;
			localStorage.setItem(SHORTCUT_PRESET_NAME_KEY, presetName);
			persistBindings(this.bindings);
			this.privateSyncShortcutSettings(commandBus);
		},
		applyShortcutSettingsPreference(rawPreference: string): void {
			let parsed: unknown;

			try {
				parsed = JSON.parse(rawPreference);
			} catch {
				return;
			}

			if (typeof parsed !== 'object' || parsed === null || !('bindings' in parsed) || !('enabled' in parsed)) {
				return;
			}

			if (typeof parsed.enabled !== 'boolean') {
				return;
			}

			const presetName = resolveShortcutPresetName('presetName' in parsed ? parsed.presetName : undefined);
			const bindings = sanitizeShortcutBindings(presetName, parsed.bindings);

			this.bindings = bindings;
			this.enabled = parsed.enabled;
			this.presetName = presetName;

			localStorage.setItem(SHORTCUT_ENABLED_KEY, String(parsed.enabled));
			localStorage.setItem(SHORTCUT_PRESET_NAME_KEY, presetName);
			persistBindings(bindings);
		},
		importBindings(rawJson: string, commandBus?: CommandBus): boolean {
			try {
				const parsed = JSON.parse(rawJson) as unknown;

				if (typeof parsed !== 'object' || parsed === null) {
					return false;
				}

				const candidate: Record<string, { enabled?: unknown; keys?: unknown }> = migrateBindingIds(
					parsed as Record<string, { enabled: boolean; keys: string }>
				);
				const nextBindings: ShortcutBindingsMap = { ...this.bindings };

				for (const [actionId, incoming] of Object.entries(candidate)) {
					if (!(actionId in nextBindings)) {
						continue;
					}

					if (typeof incoming.keys !== 'string' || typeof incoming.enabled !== 'boolean') {
						continue;
					}

					nextBindings[actionId] = {
						enabled: incoming.enabled,
						id: actionId,
						keys: normalizeKeys(incoming.keys),
					};
				}

				this.bindings = nextBindings;
				this.presetName = 'custom';
				localStorage.setItem(SHORTCUT_PRESET_NAME_KEY, 'custom');
				persistBindings(this.bindings);
				this.privateSyncShortcutSettings(commandBus);
				return true;
			} catch {
				return false;
			}
		},
		privateSyncShortcutSettings(commandBus: CommandBus | undefined): void {
			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					SHORTCUT_SETTINGS_PREFERENCE_KEY,
					JSON.stringify({
						bindings: this.bindings,
						enabled: this.enabled,
						presetName: this.presetName,
					})
				)
			);
		},
		resetBindingToDefault(actionId: string, commandBus?: CommandBus): void {
			const effectivePreset: ShortcutPresetName = this.presetName === 'custom' ? 'minimal' : this.presetName;
			const defaults = buildDefaultBindings(effectivePreset);
			const fallback = defaults[actionId];

			if (fallback === undefined) {
				return;
			}

			this.bindings = {
				...this.bindings,
				[actionId]: { ...fallback },
			};
			persistBindings(this.bindings);
			this.privateSyncShortcutSettings(commandBus);
		},
		setBindingEnabled(actionId: string, enabled: boolean, commandBus?: CommandBus): void {
			const existing = this.bindings[actionId];

			if (existing === undefined) {
				return;
			}

			this.bindings = {
				...this.bindings,
				[actionId]: { ...existing, enabled },
			};

			if (this.presetName !== 'custom') {
				this.presetName = 'custom';
				localStorage.setItem(SHORTCUT_PRESET_NAME_KEY, 'custom');
			}

			persistBindings(this.bindings);
			this.privateSyncShortcutSettings(commandBus);
		},
		setBindingKeys(actionId: string, keys: string, commandBus?: CommandBus): void {
			const existing = this.bindings[actionId];

			if (existing === undefined) {
				return;
			}

			const normalized = normalizeKeys(keys);

			this.bindings = {
				...this.bindings,
				[actionId]: {
					...existing,
					enabled: normalized.length > 0,
					keys: normalized,
				},
			};

			if (this.presetName !== 'custom') {
				this.presetName = 'custom';
				localStorage.setItem(SHORTCUT_PRESET_NAME_KEY, 'custom');
			}

			persistBindings(this.bindings);
			this.privateSyncShortcutSettings(commandBus);
		},
		setShortcutsEnabled(enabled: boolean, commandBus?: CommandBus): void {
			this.enabled = enabled;
			localStorage.setItem(SHORTCUT_ENABLED_KEY, String(enabled));
			this.privateSyncShortcutSettings(commandBus);
		},
	},
	getters: {
		conflicts(state): ShortcutConflictEntry[] {
			return findShortcutConflicts(state.bindings).filter((entry) => entry.severity === 'conflict');
		},
		hasConflicts(): boolean {
			return this.conflicts.length > 0;
		},
	},
	state: createDefaultState,
});

export * from './types';
